import type { ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Lottie } from 'lottie-react'

import forgotPasswordSuccess from '../../../assets/forgot_password_success.png'
import { AuthCarousel } from './AuthCarousel'
import logoLeft from '../../../assets/auth/logo/ledryt-logo1.json'
import logoRight from '../../../assets/auth/logo/ledryt-logo-cc2.json'

type ToastVariant = 'error' | 'success'

type AuthToastProps = {
  message: string | null
  variant: ToastVariant
  closing: boolean
  onClose: () => void
}

type AuthPageShellProps = {
  contentMaxWidth?: string
  children: ReactNode
}

type LoginFlowSuccessStateProps = {
  imageAlt: string
  message: string
  linkTo: string
  linkLabel: string
}

export function AuthToast({ message, variant, closing, onClose }: AuthToastProps) {
  if (!message) {
    return null
  }

  return (
    <div className="pointer-events-none fixed right-0 top-0 z-50 w-full max-w-[18.5rem] p-3 sm:max-w-[19.5rem] sm:p-5">
      <div
        className={`pointer-events-auto flex items-center gap-2.5 rounded-[8px] border bg-white px-2.5 py-2 shadow-[0_6px_18px_rgba(0,0,0,0.06)] ${
          variant === 'success' ? 'border-[#d8eedf]' : 'border-[#f0e9e9]'
        } ${closing ? 'login-toast-exit' : 'login-toast-enter'}`}
      >
        <div
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] ${
            variant === 'success' ? 'bg-[#e9f8ef]' : 'bg-[#fff1f1]'
          }`}
        >
          <AlertCircle className={`h-3 w-3 ${variant === 'success' ? 'text-[#2ba35f]' : 'text-[#f05b5b]'}`} />
        </div>
        <p className="flex-1 text-btn-tiny font-medium leading-[1.3] text-[#4b4b4b] sm:text-btn-small sm:font-medium">
          {message}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 pl-1 text-s2 font-normal leading-none text-[#6a6a6a] transition hover:text-black"
          aria-label="Close notification"
        >
          x
        </button>
      </div>
    </div>
  )
}

export function LoginFlowSuccessState({ imageAlt, message, linkTo, linkLabel }: LoginFlowSuccessStateProps) {
  return (
    <div className="flex w-full max-w-[520px] flex-col items-center text-center">
      <img
        src={forgotPasswordSuccess}
        alt={imageAlt}
        className="w-full max-w-[260px] select-none object-contain sm:max-w-[280px]"
      />

      <p className="mt-4 text-sm font-medium tracking-[-0.01em] text-gray-700">
        {message}{' '}
        <Link
          to={linkTo}
          className="font-semibold text-[#146971] underline underline-offset-[2px] transition hover:text-[#0f545a]"
        >
          {linkLabel}
        </Link>
      </p>
    </div>
  )
}

export function AuthPageShell({ children }: AuthPageShellProps) {
  return (
    <div className="h-screen max-h-screen w-full flex flex-col md:flex-row bg-white antialiased font-sans overflow-hidden">
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes progress {
              from { width: 0%; }
              to { width: 100%; }
            }

            @keyframes login-shake {
              0%, 100% { transform: translateX(0); }
              25% { transform: translateX(-4px); }
              75% { transform: translateX(4px); }
            }

            .login-shake {
              animation: login-shake 0.24s ease-in-out 2;
            }

            @keyframes toast-slide-in {
              from { opacity: 0; transform: translateX(28px); }
              to { opacity: 1; transform: translateX(0); }
            }

            @keyframes toast-slide-out {
              from { opacity: 1; transform: translateX(0); }
              to { opacity: 0; transform: translateX(28px); }
            }

            .login-toast-enter { animation: toast-slide-in 220ms ease-out forwards; }
            .login-toast-exit { animation: toast-slide-out 220ms ease-in forwards; }
          `,
        }}
      />

      {/* Left Panel: Desktop only */}
      <div className="hidden md:block md:w-[52%] lg:w-[55%] h-screen overflow-hidden">
        <AuthCarousel>
          {/* Top Left Logo — direct transparent Lottie mark over photo */}
          <div className="absolute top-5 left-6 z-30 w-[130px] h-[48px] bg-transparent flex items-center justify-center pointer-events-auto">
            <Lottie
              src={logoLeft}
              autoplay={true}
              loop={true}
              className="w-full h-full object-contain bg-transparent"
            />
          </div>
        </AuthCarousel>
      </div>

      {/* Right Panel: Form view */}
      <div className="w-full md:w-[48%] lg:w-[45%] h-screen flex flex-col items-center justify-center py-4 px-4 md:px-8 overflow-y-auto bg-[#fcfefe]">
        <div className="w-[390px] max-w-full mx-auto flex flex-col my-auto">

          {/* Header Block */}
          <div className="text-center mb-4">
            {/* Main Form Header Logo */}
            <div className="w-[140px] h-[56px] mx-auto mb-1.5 bg-transparent flex items-center justify-center">
              <Lottie
                src={logoRight}
                autoplay={true}
                loop={true}
                className="w-full h-full object-contain bg-transparent"
              />
            </div>

            <h1 className="text-[26px] lg:text-[28px] font-bold text-[#111827] tracking-[-0.6px] leading-[32px] mb-0.5 text-center">
              Welcome to LEDryt
            </h1>
            <p className="text-[12px] lg:text-[13px] text-[#6b7280] font-normal text-center">
              Leading Education in the Right Direction.
            </p>
          </div>

          {children}

        </div>
      </div>
    </div>
  )
}

